import React, { useEffect, useMemo, useRef, useState } from 'react';

interface ComboBoxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
  required?: boolean;
  uppercase?: boolean;
  leadingIcon?: React.ReactNode;
}

export function ComboBox({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  required,
  uppercase,
  leadingIcon,
}: ComboBoxProps) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // Filtering only kicks in once the user types after opening — opening via
  // focus/click always shows the full list, even if the field already has a
  // value (otherwise a pre-filled field would hide every other option).
  const [isFiltering, setIsFiltering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!isFiltering) return options;
    const q = value.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, value, isFiltering]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectOption = (opt: string) => {
    onChange(uppercase ? opt.toUpperCase() : opt);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && highlighted >= 0 && filtered[highlighted]) {
        e.preventDefault();
        selectOption(filtered[highlighted]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        {leadingIcon}
        <input
          type="text"
          className={className}
          placeholder={placeholder}
          value={value}
          required={required}
          autoComplete="off"
          onChange={(e) => {
            onChange(uppercase ? e.target.value.toUpperCase() : e.target.value);
            setOpen(true);
            setIsFiltering(true);
            setHighlighted(-1);
          }}
          onFocus={() => {
            setOpen(true);
            setIsFiltering(false);
          }}
          onKeyDown={handleKeyDown}
        />
        {options.length > 0 && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => {
              setOpen((o) => !o);
              setIsFiltering(false);
            }}
            aria-label="Toggle options"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-dim)] hover:text-[var(--text-main)] transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-lg">
          {filtered.map((opt, i) => (
            <li
              key={opt}
              className={`px-3 py-2 text-sm font-mono cursor-pointer ${
                i === highlighted ? 'bg-[var(--accent-light)] text-[var(--accent-color)]' : 'hover:bg-[var(--card-bg-subtle)]'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectOption(opt);
              }}
              onMouseEnter={() => setHighlighted(i)}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
